import React from 'react';
import { 
  CalendarIcon, 
  ClockIcon, 
  UserGroupIcon, 
  VideoCameraIcon,
  AcademicCapIcon,
  MapPinIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { ClassSchedule } from '../types';

interface ClassScheduleCardProps {
  schedule: ClassSchedule;
}

const ClassScheduleCard: React.FC<ClassScheduleCardProps> = ({ schedule }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      fullDate: date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ongoing': return 'bg-green-100 text-green-800 border-green-200';
      case 'completed': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled': return <ClockIcon className="h-4 w-4" />;
      case 'ongoing': return <CheckCircleIcon className="h-4 w-4" />;
      case 'completed': return <CheckCircleIcon className="h-4 w-4" />;
      case 'cancelled': return <XCircleIcon className="h-4 w-4" />;
      default: return <ClockIcon className="h-4 w-4" />;
    }
  };

  const { date, time, fullDate } = formatDate(schedule.scheduledTime);
  const isUpcoming = new Date(schedule.scheduledTime) > new Date();
  const isToday = new Date(schedule.scheduledTime).toDateString() === new Date().toDateString();

  return (
    <div className={`bg-white rounded-xl shadow-lg border-2 transition-all duration-200 hover:shadow-xl ${
      isToday ? 'border-orange-300 bg-orange-50' : 'border-gray-200'
    }`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-1">{schedule.classroomName}</h3>
            <p className="text-sm text-gray-600">{schedule.subject}</p>
          </div>
          
          {/* Status Badge */}
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(schedule.status)}`}>
            {getStatusIcon(schedule.status)}
            <span className="ml-1 capitalize">{schedule.status}</span>
          </span>
        </div>

        {/* Date and Time */}
        <div className="flex items-center space-x-4 text-sm">
          <div className="flex items-center text-gray-600">
            <CalendarIcon className="h-4 w-4 mr-1" />
            <span className={isToday ? 'font-semibold text-orange-600' : ''}>
              {isToday ? 'Today' : date}
            </span>
          </div>
          <div className="flex items-center text-gray-600">
            <ClockIcon className="h-4 w-4 mr-1" />
            <span className={isToday ? 'font-semibold text-orange-600' : ''}>
              {time}
            </span>
          </div>
          <div className="flex items-center text-gray-600">
            <ClockIcon className="h-4 w-4 mr-1" />
            <span>{schedule.duration} min</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Description */}
        {schedule.description && (
          <p className="text-gray-700 text-sm mb-4 leading-relaxed">
            {schedule.description}
          </p>
        )}

        {/* Instructor */}
        <div className="flex items-center space-x-2 mb-4">
          <AcademicCapIcon className="h-4 w-4 text-primary-600" />
          <span className="text-sm text-gray-600">
            <span className="font-medium">Instructor:</span> {schedule.instructorName}
          </span>
        </div>

        {/* Participants */}
        <div className="flex items-center space-x-2 mb-4">
          <UserGroupIcon className="h-4 w-4 text-primary-600" />
          <span className="text-sm text-gray-600">
            <span className="font-medium">Participants:</span> {schedule.currentParticipants}/{schedule.maxParticipants}
          </span>
        </div>

        {/* Category */}
        <div className="flex items-center space-x-2 mb-4">
          <MapPinIcon className="h-4 w-4 text-primary-600" />
          <span className="text-sm text-gray-600">
            <span className="font-medium">Program:</span> {schedule.category}
          </span>
        </div>

        {/* Materials */}
        {schedule.materials && schedule.materials.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Materials</h4>
            <div className="flex flex-wrap gap-2">
              {schedule.materials.map((material, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                >
                  📎 {material}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Recurring Info */}
        {schedule.isRecurring && schedule.recurringPattern && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-blue-800">
                <span className="font-medium">Recurring:</span> {schedule.recurringPattern} schedule
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-gray-100 bg-gray-50">
        <div className="flex items-center justify-between">
          {/* Meeting Link */}
          {schedule.meetingLink && (
            <a
              href={schedule.meetingLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              <VideoCameraIcon className="h-4 w-4 mr-2" />
              Join Meeting
            </a>
          )}

          {/* Full Date Info */}
          <div className="text-xs text-gray-500 text-right">
            <div>{fullDate}</div>
            <div>{schedule.duration} minutes</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassScheduleCard;
